import { Directive } from "@app/components/directive";
import { ITreeNodeScope } from "@app/components/kb-tree/kb-tree-node.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { eScrollMode, Utils } from "@tools";
import * as angular from "angular";

export interface ITreeScope extends ng.IScope {
    /** 2 way binding that holds the currently selected data item of the tree */
    selectedItem: any;
    /** default is false. Makes it such that only one item can be expanded at a time.  Not recommended for d&d */
    accordion: boolean;
    /** default is true. Set to false to make nodes not selectable */
    selectable: boolean;
    horizontal: boolean;
    /** default is true.  Set to false to have all nodes expanded with no ability to collapse */
    expandable: boolean;
    /** set to the name of an angular animation (ex: 'kb-rise') */
    animation: string;
    /** 'cards' makes it look like admin tool, 'checks' adds checkboxes */
    skin: string;
    /** default is false. Set to true to allow d&d.   */
    dragDrop: boolean;
    /** 'material' will have selection line */
    selectionStyle: string;
    /** a class that will be applied to selected nodes */
    selectionClass: string;
    /** the color of the selection line if the selection mode is 'line'.  Options are:  accent, primary, contrast */
    selectionLineColor: string;
    /** controls the tab width mode when horizontal is true.  Options are 'fill', 'auto', and 'custom' */
    tabWidth: string;
    /** required for d&d.  Should return empty string for no drop, "sibling", or "child", or "any" */
    allowDrop: () => (target, source) => string;
    /** required for d&d.  Gives the consumer information about the drop so they can modify the model accordingly */
    dropped: () => (target, source, targetParent, sourceParent, dropPosition: string) => void;
    /** by default kbTree defers loading of nodes until the parent node is expanded.  
     * This setting forces it to load all nodes at once 
     */
    // loadAllNodes: boolean;

    // internal scope properties
    _selectedNode: ITreeNodeScope;
    _nodes: ITreeNodeScope[];
    _dragNode: ITreeNodeScope; // internal.  The node being dragged
    _currentDragTarget: HTMLElement;
}

export interface ITreeController {
    scope: ITreeScope;
    registerNode: (node: ITreeNodeScope) => void;
    setCurrentDragTarget: (el: HTMLElement) => void;
    removeDropClasses: ($target: angular.IAugmentedJQuery) => void;
    refreshSelectionLine: () => void;
}

/**
 * KbTree
 *
 * kbTree can be used in several different ways.  
 * If you have a hierarchy of like objects that infinitely nest, you can use it like so:
 * 
 *   <script type="text/ng-template" id="cat-child-template">
 *     <kb-tree-node ng-repeat="cat in cat.items" 
 *                 item="cat" 
 *                 child-template="cat-child-template">
 *     </kb-tree-node>
 * </script>
 * 
 * <kb-tree>
 *     <kb-tree-node ng-repeat="cat in categories" 
 *                     item="cat" 
 *                     child-template="cat-child-template">
 *     </kb-tree-node>       
 * </kb-tree>
 *     
 * in this case you are providing a child template to the tree-node directive, 
 * which refers to itself.  In this way we achieve
 * infinite nesting.
 * 
 * If you do NOT need infinite nesting, then you can just add tree-nodes as content 
 * of other tree-nodes, while NOT providing
 * a child template.  Like so:
 * 
 * <<kb-tree>
 *     <kb-tree-node ng-repeat="page in pages" item="page">
 *         <kb-tree-node ng-repeat="group in page.groups">
 *         </kb-tree-node>
 *     </kb-tree-node>       
 * </kb-tree>
 *
 */

@NgComponent({
    token: Token.KbTree,
    dependencies: [
    ]
})
export class KbTree extends Directive {
    constructor() {

        super();

        this.replace = true;
        this.template = Dirs.component("kb-tree");
        this.restrict = "AE";
        this.scope = {
            selectedItem: "=?",
            accordion: "=?",
            skin: "@",
            selectable: "=?",
            expandable: "=?",
            dragDrop: "=?",
            allowDrop: "&?",
            dropped: "&?",
            animation: "@",
            // loadAllNodes: "=?",
            horizontal: "=?",
            selectionStyle: "=?",
            selectionClass: "=?",
            selectionLineColor: "=?",
            tabWidth: "=?"
            // control: "=?"
        };
        this.transclude = true;

        // add scope defaults to the compile function
        this.compile = (element, atts: any) => {
            if (!angular.isDefined(atts.selectable)) atts.selectable = "true";
            if (!angular.isDefined(atts.expandable)) atts.expandable = "true";
            if (!angular.isDefined(atts.selectionStyle)) atts.selectionStyle = "'background'";
            if (!angular.isDefined(atts.selectionLineColor)) atts.selectionLineColor = "'accent'";
            if (!angular.isDefined(atts.tabWidth)) atts.tabWidth = "'auto'";
        };

        this.controller = [
            "$scope",
            "$element",
            "$attrs",
            "$transclude",
            function (
                $scope: ITreeScope,
                $element: ng.IAugmentedJQuery,
                $attrs: ng.IAttributes,
                $transclude: ng.ITranscludeFunction) {
                // set the scope of the tree on it's controller so children can access it
                this.scope = $scope;
                $scope._nodes = [];

                // $scope.control = $scope.control || {};
                // $scope.control.expandToNode = (item) => {
                //    $scope._nodes.forEach(n => setChildrenValid(n));
                // };

                // all child nodes register themselves with the tree to handle selection and accordion functionality
                this.registerNode = (node: ITreeNodeScope) => {
                    $scope._nodes.push(node);
                    let off = node.$on("$destroy", event => {
                        $scope._nodes.remove(node);
                        off();
                    });
                };

                this.setCurrentDragTarget = (el: HTMLElement) => {
                    if (el !== $scope._currentDragTarget && $scope._currentDragTarget) {
                        this.removeDropClasses($($scope._currentDragTarget));
                        console.log({ event: "currentDragChanged", el });
                    }
                    $scope._currentDragTarget = el;
                };

                this.removeDropClasses = ($target: angular.IAugmentedJQuery) => {
                    $target.removeClass("kb-tree--dropping-child");
                    $target.removeClass("kb-tree--dropping-sibling-below");
                    $target.removeClass("kb-tree--dropping-sibling-above");
                };

                let updateSelectedNode = () => {
                    for (let node of $scope._nodes) {
                        if (node.item === $scope.selectedItem) {
                            let origSelected = $scope._selectedNode;
                            $scope._selectedNode = node;
                            scrollToSelectedNode();
                            refreshSelectionLine();
                            // expand all parent nodes when a node is selected
                            node._getAncestorNodes(false).forEach(n => n.expanded = true);
                            break;
                        }
                    }
                };
                // if the consumer changes the selected item, we update the selected node
                $scope.$watch("selectedItem", () => {
                    updateSelectedNode();
                });
                // nodes might be added after the selectedItem is set by the consumer, so
                // we need to refind the selected node when the nodes collection changes
                $scope.$watchCollection("_nodes", () => {
                    if (!$scope._selectedNode) {
                        updateSelectedNode();
                    }
                });

                $scope.$watch("expandable", () => {
                    if (!$scope.expandable) {
                        $scope._nodes.forEach(n => expandNodeAndChildren(n));
                        refreshSelectionLine();
                    }
                });

                $scope.$watch("horizontal", () => {
                    setTimeout(() => refreshSelectionLine(), 0);
                });

                $scope.$watch("selectionStyle", () => {
                    setTimeout(() => refreshSelectionLine(), 0);
                });

                $scope.$watch("tabWidth", () => {
                    setTimeout(() => refreshSelectionLine(), 0);
                });

                let expandNodeAndChildren = (node: ITreeNodeScope) => {
                    node.expanded = true;
                    node._children.forEach(child => expandNodeAndChildren(child));                    
                };

                let scrollToSelectedNode = () => {
                    setTimeout(() => { //the call to JQuery.position() below causes a forced layout. We defer to next $digest so we're not slowing down the page loading in running configurators
                        if ($scope._selectedNode) {
                            let $node = $scope._selectedNode._getHeaderElement();
                            if ($node && $node.length) {
                                Utils.scrollIntoView({ elem: $node[0], mode: eScrollMode.nearest, animate: true, horizontal: $scope.horizontal });
                            }
                        }

                    }, 0);
                };

                let $selectionLine: angular.IAugmentedJQuery;
                let refreshSelectionLine = this.refreshSelectionLine = () => {
                    if ($scope._selectedNode && $scope.selectionStyle && $scope.selectionStyle.isEqual("line")) {
                        if (!$selectionLine || !$selectionLine.length) {
                            $selectionLine = $('<div/>', {
                                class: "kb-tree__selection-line",
                                appendTo: $element
                            });
                        }
                        let $node = $scope._selectedNode._getElement();
                        let nodeEl = $node.find(".kb-tree__header").first()[0];

                        if ($scope.horizontal) {
                            $selectionLine.css({ left: nodeEl.offsetLeft, width: nodeEl.offsetWidth, bottom: 0, height: "2px", top: "auto" });
                        } else {
                            $selectionLine.css({ top: nodeEl.offsetTop, height: nodeEl.offsetHeight, left: 0, width: "2px", bottom: "auto" });
                        }                        
                    } else {
                        if ($selectionLine) $selectionLine.remove();
                        $selectionLine = null;
                    }
                };
            }];
    }

}
